import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChefHat, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO } from "@/lib/brand";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", restaurant_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Your 14-day free trial has started!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiErr(err));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-block mb-8">
            <img src={LOGO} alt="manage.kitchen" className="h-16 w-auto object-contain" />
          </Link>
          <h1 className="font-heading text-3xl font-medium">Start your free trial</h1>
          <p className="text-muted-foreground mt-1 text-sm">14 days free. No credit card required.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Your name</Label>
              <Input id="name" data-testid="register-name" required value={form.name} onChange={set("name")} placeholder="Maria Lopez" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="restaurant">Restaurant name</Label>
              <Input id="restaurant" data-testid="register-restaurant" required value={form.restaurant_name} onChange={set("restaurant_name")} placeholder="The Copper Skillet" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="register-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@restaurant.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="At least 6 characters" />
            </div>
            <Button data-testid="register-submit" type="submit" disabled={loading}
              className="w-full bg-primary hover:bg-[#B35A42] text-primary-foreground">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline">Log in</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://images.pexels.com/photos/17086288/pexels-photo-17086288.jpeg" alt=""
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-10 left-10 text-[#FDFBF7]">
          <h2 className="font-heading text-4xl font-medium">Everything in one place.</h2>
          <p className="mt-2 text-[#FDFBF7]/80 max-w-md">Menus, staff, inventory, sales and AI forecasting from day one.</p>
        </div>
      </div>
    </div>
  );
}
